import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './lib/auth';
import { RequireAuth, NoDoctores, SoloAdmin, SoloGestion, SoloDireccion } from './lib/RequireAuth';
import { useAuth } from './lib/auth';
import { isDoctor, isAnestesista } from './lib/roles';
import AppShell from './components/AppShell';
import LoginPage from './pages/LoginPage';
import PublicLopdPage from './pages/PublicLopdPage';
import GuardianPuntero from './components/GuardianPuntero';
import PreSaludPage from './pages/PreSaludPage';
import ConfirmarCitaPage from './pages/ConfirmarCitaPage';
import PublicConsentPage from './pages/PublicConsentPage';
import PublicConsentLinkPage from './pages/PublicConsentLinkPage';
import DashboardPage from './pages/DashboardPage';
import PatientsPage from './pages/PatientsPage';
import PatientDetailPage from './pages/PatientDetailPage';
import BudgetsPage from './pages/BudgetsPage';
import LopdPage from './pages/LopdPage';
import PreopPage from './pages/PreopPage';
import InstructionsPage from './pages/InstructionsPage';
import ConsentsPage from './pages/ConsentsPage';
import RecipesPage from './pages/RecipesPage';
import JustificationsPage from './pages/JustificationsPage';
import CobrosPage from './pages/CobrosPage';
import DatafonoPage from './pages/DatafonoPage';
import ColaboradoresPage from './pages/ColaboradoresPage';
import SurgeryLogPage from './pages/SurgeryLogPage';
import CatalogPage from './pages/CatalogPage';
import ConsentTemplatesPage from './pages/ConsentTemplatesPage';
import PostopPage from './pages/PostopPage';
import MarketingPage from './pages/MarketingPage';
import ControlRastreoPage from './pages/ControlRastreoPage';
import DrivePage from './pages/DrivePage';
import WhatsAppPage from './pages/WhatsAppPage';
import MailPage from './pages/MailPage';
import AgentePage from './pages/AgentePage';
import DevolucionesPage from './pages/DevolucionesPage';
import FacturacionPage from './pages/FacturacionPage';
import DatosEstudioPage from './pages/DatosEstudioPage';
import IndicadoresPage from './pages/IndicadoresPage';
import TareasDireccionPage from './pages/TareasDireccionPage';
import LeadsPage from './pages/LeadsPage';
import ChatPage from './pages/ChatPage';
import ProtocolosPage from './pages/ProtocolosPage';
import MisPasosCirugiaPage from './pages/MisPasosCirugiaPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import SaludEnlacesPage from './pages/SaludEnlacesPage';
import ImportPacientesPage from './pages/ImportPacientesPage';
import InformesPage from './pages/InformesPage';
import CitasPage from './pages/CitasPage';
import QuirofanoSesionesPage from './pages/QuirofanoSesionesPage';
import AnestesiaPage from './pages/AnestesiaPage';
import RegistroExternosPage from './pages/RegistroExternosPage';
import GestionAgendasPage from './pages/GestionAgendasPage';
import RegistroJornadaPage from './pages/RegistroJornadaPage';
import { esRutaPublica, getTheme } from './lib/theme';
import './App.css';

/**
 * Las páginas del paciente van siempre en claro (2026-07-26): la firma es un
 * trazo oscuro sobre lienzo blanco y con el tema oscuro no se veía.
 * `index.html` ya lo resuelve al abrir el enlace directo, pero el kiosco de la
 * tablet entra desde el login sin recargar la página, así que hace falta
 * también aquí. Al salir se recupera la preferencia guardada.
 */
function TemaSegunRuta() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (esRutaPublica(pathname)) document.documentElement.classList.remove('dark');
    else document.documentElement.classList.toggle('dark', getTheme() === 'dark');
  }, [pathname]);
  return null;
}

/** Portada según rol: los doctores aterrizan en su agenda, no en el
 * dashboard administrativo. */
function Portada() {
  const { user } = useAuth();
  if (isDoctor(user)) return <Navigate to="/citas" replace />;
  if (isAnestesista(user)) return <Navigate to="/quirofano-sesiones" replace />;
  return <DashboardPage />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TemaSegunRuta />
        <GuardianPuntero />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/firma-lopd" element={<PublicLopdPage />} />
          <Route path="/salud/:token" element={<PreSaludPage />} />
          <Route path="/mis-pasos/:token" element={<MisPasosCirugiaPage />} />
          {/* El mismo enlace cortado justo tras la barra (`/mis-pasos/`,
              `/salud/`) caía en el comodín y acababa en el login del
              PERSONAL. La página, sin token, dice que está incompleto y da
              el teléfono de la clínica (23/9/2026). */}
          <Route path="/salud" element={<PreSaludPage />} />
          <Route path="/mis-pasos" element={<MisPasosCirugiaPage />} />
          <Route path="/confirmar-cita" element={<ConfirmarCitaPage />} />
          <Route path="/firma-consent" element={<PublicConsentPage />} />
          <Route path="/firma-consent-link" element={<PublicConsentLinkPage />} />
          <Route
            path="/"
            element={<RequireAuth><AppShell /></RequireAuth>}
          >
            <Route index element={<Portada />} />
            {/* Rutas clínicas — accesibles también para doctores */}
            <Route path="citas" element={<CitasPage />} />
            <Route path="recetas" element={<RecipesPage />} />
            <Route path="informes" element={<InformesPage />} />
            <Route path="pacientes/:id" element={<PatientDetailPage />} />
            {/* Redirecciones de las rutas legacy (Agenda PDF / Quirófano docs) */}
            <Route path="agenda" element={<Navigate to="/citas" replace />} />
            <Route path="quirofano" element={<Navigate to="/quirofano-sesiones" replace />} />
            {/* Resto de la app — fuera del alcance del rol doctor */}
            {/* 2026-07-25: los doctores también ven el parte de quirófano
                (sus días de cirugía); crear/editar sigue siendo de gestión. */}
            <Route path="quirofano-sesiones" element={<QuirofanoSesionesPage />} />
            <Route path="anestesia" element={<NoDoctores><AnestesiaPage /></NoDoctores>} />
            <Route path="registro-externos" element={<SoloAdmin><RegistroExternosPage /></SoloAdmin>} />
            <Route path="pacientes" element={<NoDoctores><PatientsPage /></NoDoctores>} />
            <Route path="importar-pacientes" element={<NoDoctores><ImportPacientesPage /></NoDoctores>} />
            {/* Presupuestos, solo gestión, como su API desde el 2026-08-18
                (Dirección: «nadie que no sea administrativos o administradores
                puede ver presupuestos») y como el menú. Con `NoDoctores`,
                enfermería o anestesia entraban por URL a una pantalla que al
                cargar daba 403, y cuyo alta de reserva hace POST y DELETE a
                /api/cobros, que desde D2 (22/9/2026) también es de gestión. */}
            <Route path="presupuestos" element={<SoloGestion><BudgetsPage /></SoloGestion>} />
            <Route path="lopd" element={<NoDoctores><LopdPage /></NoDoctores>} />
            <Route path="preoperatorios" element={<NoDoctores><PreopPage /></NoDoctores>} />
            <Route path="consentimientos" element={<NoDoctores><ConsentsPage /></NoDoctores>} />
            <Route path="indicaciones" element={<NoDoctores><InstructionsPage /></NoDoctores>} />
            <Route path="postoperatorios" element={<NoDoctores><PostopPage /></NoDoctores>} />
            <Route path="justificantes" element={<NoDoctores><JustificationsPage /></NoDoctores>} />
            {/* Cobros, Datáfono y «Facturas y devoluciones»: solo gestión
                (D2, 22/9/2026: «enfermería no necesita cobros»). El
                servidor ya lo exige —`_staff_cobros` es `require_gestion()`,
                la misma puerta que el listado y las devoluciones—; esto solo
                evita montarle a enfermería o a un clínico una pantalla que al
                pulsar da 403. El Datáfono va con Cobros porque es un alta de
                cobro. Quién ve QUÉ del listado no se decide aquí —una ruta no
                sabe de meses ni de días—: lo decide el servidor
                (`_caja_del_dia_o_nada`), que a las administrativas solo les
                da la caja de un día. */}
            <Route path="cobros" element={<SoloGestion><CobrosPage /></SoloGestion>} />
            <Route path="datafono" element={<SoloGestion><DatafonoPage /></SoloGestion>} />
            <Route path="colaboradores" element={<ColaboradoresPage />} />
            <Route path="devoluciones" element={<SoloGestion><DevolucionesPage /></SoloGestion>} />
            {/* Facturación de la sociedad (Vistaláser, 7/10/2026): administración y
                dirección, como el servidor (facturacion_router, require_management). */}
            <Route path="facturacion" element={<SoloDireccion><FacturacionPage /></SoloDireccion>} />
            {/* Datos para estudio (oftalmología, 4/11/2026): medicina, dirección y
                administración; el servidor lo cierra igual (oftalmologia_router). */}
            <Route path="datos-estudio" element={<DatosEstudioPage />} />
            <Route path="registro-quirurgico" element={<NoDoctores><SurgeryLogPage /></NoDoctores>} />
            <Route path="registro-jornada" element={<NoDoctores><RegistroJornadaPage /></NoDoctores>} />
            <Route path="catalogo-cirugias" element={<NoDoctores><CatalogPage /></NoDoctores>} />
            <Route path="plantillas-consentimientos" element={<NoDoctores><ConsentTemplatesPage /></NoDoctores>} />
            <Route path="gestion-agendas" element={<NoDoctores><GestionAgendasPage /></NoDoctores>} />
            {/* Usuarios y Marketing, solo administradores (M32, informe del
                22/9/2026): el menú ya los enseñaba solo al admin y el
                servidor es `require_role("admin")`; con `NoDoctores`, quien
                entraba por URL llegaba a la pantalla vacía y a un 403. */}
            <Route path="usuarios" element={<SoloAdmin><UsersPage /></SoloAdmin>} />
            <Route path="marketing" element={<SoloAdmin><MarketingPage /></SoloAdmin>} />
            <Route path="control-rastreo" element={<SoloAdmin><ControlRastreoPage /></SoloAdmin>} />
            <Route path="indicadores" element={<SoloAdmin><IndicadoresPage /></SoloAdmin>} />
            <Route path="tareas-direccion" element={<SoloAdmin><TareasDireccionPage /></SoloAdmin>} />
            {/* Leads/Contactos (2026-09-22): los atiende administración. El
                servidor exige el mismo rol —eso es la regla—; esto evita
                montarle la pantalla entera a quien va a recibir un 403. */}
            <Route path="leads" element={<SoloGestion><LeadsPage /></SoloGestion>} />
            {/* Chat interno (23/9/2026): administración y dirección. Qué salas
                ve cada uno lo decide el servidor por rol (la de dirección,
                solo dirección); esto evita montarle la pantalla a quien va a
                recibir un 403. */}
            <Route path="chat" element={<SoloGestion><ChatPage /></SoloGestion>} />
            <Route path="protocolos" element={<ProtocolosPage />} />
            <Route path="drive" element={<SoloAdmin><DrivePage /></SoloAdmin>} />
            <Route path="whatsapp" element={<SoloGestion><WhatsAppPage /></SoloGestion>} />
            <Route path="mail" element={<SoloGestion><MailPage /></SoloGestion>} />
            <Route path="agente" element={<SoloAdmin><AgentePage /></SoloAdmin>} />
            <Route path="configuracion" element={<NoDoctores><SettingsPage /></NoDoctores>} />
            {/* Comprobación diaria de los enlaces de pacientes (2026-09-06): la
                campana lleva aquí cuando falla o lleva dos días sin hacerse. */}
            <Route path="salud-enlaces" element={<NoDoctores><SaludEnlacesPage /></NoDoctores>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
